import React, { useEffect, useRef, useState } from "react";

// Function to get color based on status
const getColor = (status) => {
  switch (status) {
    case "PENDING":
      return "#FAD7A0"; // Light Orange
    case "APPROVED":
      return "#A9DFBF"; // Light Green
    case "REJECTED":
      return "#F5B7B1"; // Light Red
    default:
      return "#D5DBDB"; // Light Gray
  }
};

const CustomTimeline = ({ rfqData }) => {
  const [sectionHeights, setSectionHeights] = useState([]);
  const containerRefs = useRef([]);
  const istDateTimeFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Kolkata",
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

  useEffect(() => {
    // Measure each section's height dynamically
    const heights = containerRefs.current.map((el) => el?.offsetHeight || 0);
    setSectionHeights(heights);
  }, []);

  // Calculate total height
  const totalHeight = sectionHeights.reduce((sum, h) => sum + h, 0);

  // Generate dynamic gradient stops
  let accumulatedHeight = 0;
  const gradientStops = sectionHeights
    ?.map((height, index) => {
      const start = (accumulatedHeight / totalHeight) * 100;
      accumulatedHeight += height;
      const end = (accumulatedHeight / totalHeight) * 100;
      return `${getColor(rfqData?.approvalDetails[index]?.status)} ${start}%, ${getColor(rfqData?.approvalDetails[index]?.status)} ${end}%`;
    })
    .join(", ");

  const formatDateTime = (dateString) => {
  if (!dateString) return "--";

  return istDateTimeFormatter.format(new Date(dateString));
};

  const getPendingSince = (dateString) => {
    if (!dateString) return "--";

    const now = new Date();
    const reminderDate = new Date(dateString);

    const diffMs = now - reminderDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) return `+${diffDays} day${diffDays > 1 ? "s" : ""}`;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours > 0) return `+${diffHours} hour${diffHours > 1 ? "s" : ""}`;

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    return `+${diffMinutes} min`;
  };

  return (
    <div className="flex flex-col w-full justify-start">
      {rfqData?.approvalDetails?.length > 0 && (
        <h2 className="text-gray-900 font-semibold ml-4 mb-1">
          Approval Timeline
        </h2>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          position: "relative",
        }}
      >
        {/* Vertical Line (Dynamically Adjusted) */}
        <div
          style={{
            position: "absolute",
            left: "10px",
            top: "0",
            width: "2px",
            alignSelf: "center",
            height: `${totalHeight}px`,
            background: `linear-gradient(to bottom, ${gradientStops})`,
          }}
        />

        {/* Timeline Items */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          {rfqData?.approvalDetails?.map((step, index) => {
            const color = getColor(step.status);
            return (
              <div
                key={step.level}
                ref={(el) => (containerRefs.current[index] = el)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                  marginBottom: "7px",
                }}
              >
                {/* Dot over the single vertical line */}
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    position: "absolute",
                    left: "6px",
                    alignSelf: "center",
                  }}
                />

                {/* Text Content with Background Color */}
                <div
                  style={{
                    marginLeft: "20px",
                    padding: "12px",
                    fontSize: "12px",
                    backgroundColor: color,
                    color: "#fff",
                    borderRadius: "6px",
                    minWidth: "260px",
                    maxWidth: "260px",
                    fontWeight: "normal",
                  }}
                >
                  {/* Level and Status */}
                  <div style={{ color: "black", fontWeight: "bold" }}>
                    Level {step.level} - {step.status}
                  </div>

                  {/* Approvers List */}
                  <div style={{ color: "black" }}>
                    Approvers: {step.approvers.map((a) => a.name).join(", ")}
                  </div>
                  {step?.requestedAt && (
                    <div
                      style={{
                        color: "black",
                      }}
                    >
                      <div>
                        <span>Received On:</span>{" "}
                        {formatDateTime(step?.requestedAt)}
                      </div>

                      {step?.status === "PENDING" && (
                        <div>
                            <span>Pending Since:</span>{" "}
                            {getPendingSince(step?.requestedAt)}
                        </div>
                        )}
                    </div>
                  )}

                  {/* Approved By (if available) */}
                  {step?.approvedBy && (
                    <div style={{ color: "black" }}>
                      Approved By: {step?.approvedBy?.name}
                    </div>
                  )}

                  {/* Approval Date and Time (if available) */}
                  {step?.approvedAt && (
                    <div style={{ color: "black" }}>
                        Approved At: {formatDateTime(step?.approvedAt)}
                    </div>
                  )}

                  {/* Approved Amount (if available) */}
                  {step?.approvedAmount && (
                    <div style={{ color: "black" }}>
                      Approved Amount: Rs.{" "}
                      {new Intl.NumberFormat("en-IN").format(
                        step.approvedAmount.toFixed(2),
                      )}
                    </div>
                  )}

                  {step?.status === "REJECTED" && step?.rejectionRemark && (
                    <div style={{ color: "black", marginTop: "5px" }}>
                      Rejection Remark: {step?.rejectionRemark}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CustomTimeline;